import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { useState, useEffect } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { useUser, type UserSession } from '../contexts/UserContext';
import LocalLogin from '../components/LocalLogin';
import { useAuthMethod } from '../hooks/useAuthMethod';

function Login() {
  const { instance } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const { loginWithLocalSession, user, loading: userLoading, emailOtpPending } = useUser();
  const { method, azureAdEnabled, localEnabled, isLoading: authMethodLoading } = useAuthMethod();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [authTab, setAuthTab] = useState<'azure' | 'local'>('azure');
  const appName = 'Portal';

  useEffect(() => {
    if (method === 'local') {
      setAuthTab('local');
    } else if (method === 'azure_ad') {
      setAuthTab('azure');
    }
  }, [method]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      setErrorMessage(decodeURIComponent(error));
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handlePasswordReset = () => {
    if (!azureAdEnabled) {
      return;
    }
    try {
      const authority = instance.getConfiguration().auth.authority;
      const isB2C = authority && authority.includes('.b2clogin.com');

      if (isB2C) {
        const authorityUrl = new URL(authority);
        const tenant = authorityUrl.hostname.split('.')[0];
        const baseAuthority = `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com`;
        const passwordResetPolicy = 'B2C_1_PasswordReset';
        const passwordResetAuthority = `${baseAuthority}/${passwordResetPolicy}`;

        instance.loginRedirect({
          ...loginRequest,
          authority: passwordResetAuthority,
        });
      } else {
        window.location.href = 'https://aka.ms/sspr';
      }
    } catch (error) {
      console.error('[Login] Password reset redirect failed:', error);
      window.location.href = 'https://aka.ms/sspr';
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const configuredRedirectUri = instance.getConfiguration().auth.redirectUri;
      const loginRequestWithRedirect = {
        ...loginRequest,
        redirectUri: configuredRedirectUri,
      };
      await instance.loginRedirect(loginRequestWithRedirect);
    } catch (error) {
      console.error('[Login] Azure AD login failed:', error);
      setIsLoading(false);
    }
  };

  const handleLocalSuccess = (accessToken: string, user: UserSession) => {
    setLocalError(null);
    loginWithLocalSession(accessToken, user);
  };

  const showAzure = azureAdEnabled;
  const showLocal = localEnabled;
  const showTabs = showAzure && showLocal;

  if (authMethodLoading) {
    return (
      <div
        className="hero-background"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontFamily: 'var(--font-family)',
        }}
      >
        <div style={{ color: '#6c757d' }}>Loading sign-in options...</div>
      </div>
    );
  }

  if (!userLoading && emailOtpPending && isMsalAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!userLoading && user && user.roles.length > 0 && !emailOtpPending) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="hero-background"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div
        className="card"
        style={{
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%',
          padding: '48px 32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <h1
          style={{
            color: 'var(--secondary-color)',
            marginBottom: '16px',
            fontSize: '2.5rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '2.5rem', opacity: 0.8 }}>
            track_changes
          </span>
        </h1>
        <h2
          style={{
            color: 'var(--secondary-color)',
            marginBottom: '8px',
            fontSize: '1.5rem',
            fontWeight: '600',
          }}
        >
          {appName}
        </h2>
        <p
          style={{
            color: '#6c757d',
            marginBottom: '32px',
            fontSize: '1.1rem',
          }}
        >
          Please sign in to continue
        </p>

        {(errorMessage || localError) && (
          <div
            style={{
              padding: '16px',
              marginBottom: '24px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '8px',
              color: '#721c24',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
              error
            </span>
            <span>{errorMessage || localError}</span>
          </div>
        )}

        {showTabs && (
          <div
            style={{
              display: 'flex',
              marginBottom: '24px',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #dee2e6',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setAuthTab('azure');
                setLocalError(null);
              }}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: authTab === 'azure' ? 'rgba(252, 80, 3, 0.12)' : 'white',
                color: authTab === 'azure' ? '#FC5003' : '#6c757d',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Microsoft
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthTab('local');
                setErrorMessage(null);
              }}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                borderLeft: '1px solid #dee2e6',
                background: authTab === 'local' ? 'rgba(252, 80, 3, 0.12)' : 'white',
                color: authTab === 'local' ? '#FC5003' : '#6c757d',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Email &amp; password
            </button>
          </div>
        )}

        {showAzure && (!showTabs || authTab === 'azure') && (
          <>
            <button
              onClick={handleLogin}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '1.1rem',
                fontWeight: '600',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: isLoading ? 'rgba(0, 0, 0, 0.1)' : 'none',
                border: isLoading ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid #FC5003',
                color: isLoading ? 'rgba(0, 0, 0, 0.4)' : '#FC5003',
                borderRadius: '8px',
              }}
            >
              <span className="material-symbols-outlined">{isLoading ? 'hourglass_empty' : 'login'}</span>
              {isLoading ? 'Signing In...' : 'Sign In with Microsoft'}
            </button>
            {azureAdEnabled && (!showTabs || authTab === 'azure') && (
              <button
                onClick={handlePasswordReset}
                type="button"
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  marginTop: '16px',
                }}
              >
                Forgot your password?
              </button>
            )}
          </>
        )}

        {showLocal && (!showTabs || authTab === 'local') && (
          <div style={{ marginTop: showAzure && showTabs ? 0 : 0 }}>
            <LocalLogin onLoginSuccess={handleLocalSuccess} onError={(m) => setLocalError(m)} />
            <Link
              to="/forgot-password"
              style={{
                display: 'block',
                width: '100%',
                marginTop: '16px',
                padding: '12px 24px',
                fontSize: '0.95rem',
                fontWeight: 500,
                textAlign: 'center',
                color: '#6c757d',
                textDecoration: 'none',
              }}
            >
              Forgot your password?
            </Link>
          </div>
        )}

        {!showAzure && !showLocal && (
          <p style={{ color: '#721c24' }}>No sign-in methods are enabled. Contact your administrator.</p>
        )}
      </div>
    </div>
  );
}

export default Login;
